/* istanbul ignore file */

import XHR from 'i18next-xhr-backend';
import i18next from 'i18next';
import ICU from 'i18next-icu';
import LngDetector from 'i18next-browser-languagedetector';
import * as KatalMetrics from '@katal/metrics';
import initialMetricsPublisher from './metrics';
import app from './app';

import languageMap from '@katal/localization/webpack-loader!';

const defaultLocaleMap = new Map();
defaultLocaleMap.set('ar', 'ar-AE');
defaultLocaleMap.set('cs', 'cs-CZ');
defaultLocaleMap.set('de', 'de-DE');
defaultLocaleMap.set('en', 'en-US');
defaultLocaleMap.set('es', 'es-ES');
defaultLocaleMap.set('fr', 'fr-FR');
defaultLocaleMap.set('he', 'he-IL');
defaultLocaleMap.set('hi', 'hi-IN');
defaultLocaleMap.set('it', 'it-IT');
defaultLocaleMap.set('ja', 'ja-JP');
defaultLocaleMap.set('kn', 'kn-IN');
defaultLocaleMap.set('ko', 'ko-KR');
defaultLocaleMap.set('ml', 'ml-IN');
defaultLocaleMap.set('nl', 'nl-NL');
defaultLocaleMap.set('pl', 'pl-PL');
defaultLocaleMap.set('pt', 'pt-BR');
defaultLocaleMap.set('sv', 'sv-SE');
defaultLocaleMap.set('ta', 'ta-IN');
defaultLocaleMap.set('te', 'te-IN');
defaultLocaleMap.set('tr', 'tr-IN');
defaultLocaleMap.set('zh', 'zh-CN');

export const i18n = i18next
    .use(ICU)
    .use(XHR)
    .use(LngDetector)
    .init(
        {
            debug: false,
            fallbackLng: 'en-US',
            load: 'currentOnly',
            backend: {
                loadPath: (localeList: any) => getLocalePath(localeList),
            },
            detection: {
                // order and from where user language should be detected
                order: ['htmlTag', 'navigator']
            }
        },
        (err, t) => {
            //initialized, now translate
            translateStrings();

            const localeDropDown = document.querySelector(
                '#changeLocaleDropdown'
            );
            if (localeDropDown) {
                localeDropDown.addEventListener('change', (event: any) => {
                    const newLocale = event.detail.value;

                    const localeChangeMetricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod("LocaleChange");
                    const localeChangeHandlerMetric = new KatalMetrics.Metric.TimedAttempt("Translation").withMonitor();
                    localeChangeMetricsPublisher.publish(new KatalMetrics.Metric.String("locale", newLocale || "NONE"));

                    document
                        .querySelector('html')!
                        .setAttribute('lang', newLocale);
                    i18next.changeLanguage(newLocale, (err, t) => {
                        if (err) {
                            localeChangeHandlerMetric.setFailure();
                        } else {
                            localeChangeHandlerMetric.setSuccess();
                        }

                        translateStrings();

                        localeChangeMetricsPublisher.publish(localeChangeHandlerMetric);
                    });
                });
            }
            app.translationInitialized(!err);
        }
    );

/**
 * Returns the path to translations inside of the website's webpack.
 * If there is no direct translation file for the given locale, this function will try to 
 * return a default path for the first 2 given language characters within the locale string.
 * If there is no default path, then it returns the path for to the en-US locale translations.
 * 
 * @param localeList Given list of locale provided by the users browser.
 * @returns String path to the translations in the generated webpack.
 */
function getLocalePath(localeList: any): string {
    let path = languageMap['en-US'];
    const givenLocale: string = localeList[0];
    if (languageMap[givenLocale] != null) {
        path = languageMap[givenLocale];
    } else if (languageMap[givenLocale] == null && givenLocale != null) {
        const defaultLocale = defaultLocaleMap.get((givenLocale.length > 2) ? givenLocale.substring(0, 2) : givenLocale);
        if (languageMap[defaultLocale] != null) {
            path = languageMap[defaultLocale];
        }
    }
    return path;
}

// Global functions. Ideally functions like this would not be needed and
// be replaced by a framework such as React or Angular.
export function translate(
    elementSelector: string,
    elementType: 'property' | 'attribute',
    elementProperty: string,
    stringId: string,
    defaultString: string,
    formatArguments?: any
) {
    if (!formatArguments) {
        formatArguments = {};
    }

    var translation = defaultString;
    // You can pass an array of keys to lookup. The last one in the list
    // will end up being the default value being displayed.
    translation = i18next.t([stringId, defaultString], formatArguments);

    const elt = document.querySelector(elementSelector);
    if (!elt) throw new Error(`No element found at path '${elementSelector}'`);

    if (elementType == 'attribute') {
        elt.setAttribute(elementProperty, translation);
    } else if (elementType === 'property') {
        (elt as any)[elementProperty] = translation;
    } else {
        throw new Error(
            `Unknown property type '${elementType}' (expected 'attribute' or 'property')`
        );
    }
}

/**
 *  !!! TEXT USING THIS METHOD DOES NOT NEED TO BE ADDED TO THE translateStrings METHOD !!!
 *  !!! IF IT DOES NOT APPEAR IN THE INDEX.HTML FILE!!!
 * 
 * A simpler translation method that is meant to return translated string
 * to any variable code in the app.ts file.
 * 
 * @param stringId Id of translated string searched for.
 * @param defaultString String returned if id is not found.
 * @param formatArguments Any arguments the string needs.
 */
export function simpleTranslate(
    stringId: string,
    defaultString?: string,
    formatArguments?: any
): string {
    defaultString = defaultString == null ? '' : defaultString;
    return i18next.t([stringId, defaultString], formatArguments);
}

/**
 * Returns default locale of given string. If it does not find a default, it will
 * return 'en-US'
 * 
 * @param localeString The given locale to get the default locale of.
 */
export function getDefaultLocale(localeString: string): string {
    return defaultLocaleMap.get(localeString) || defaultLocaleMap.get('en');
}

/**
 * Strings and string keys must be added here to be tranlasted when the web view is first loaded.
 * They should look like this:
 * 
 * translate(
 *      '#<elementSelector: id in index.html>',
 *      '<elementType: property or attribute>',
 *      '<elementProperty: text type>',
 *      '<stringId: string id in *.puff.json files>',
 *      '<defaultString: default string if id is not found in puff files>',
 *      '<formatArguments?: arguments to be added to string>'
 * );
 */
function translateStrings() {
    translate(
        '#ar-header-text-scan-code',
        'property',
        'innerText',
        'ar-header-text-scan-code',
        'Scan this QR code with your phone\'s camera to view the Alexa Routine'
    );
    translate(
        '#ar-bottom-header-need-help',
        'property',
        'innerText',
        'ar-bottom-header-need-help',
        'Need help scanning the QR code?'
    );
    translate(
        '#ar-instruction-step-1',
        'property',
        'innerText',
        'ar-instruction-step-1',
        '1. Get the Alexa app'
    );
    translate(
        '#ar-instruction-step-2',
        'property',
        'innerText',
        'ar-instruction-step-2',
        '2. Open your phone\'s camera'
    );
    translate(
        '#ar-instruction-step-3',
        'property',
        'innerText',
        'ar-instruction-step-3',
        '3. Tap the alert'
    );
    translate(
        '#ar-instruction-step-1-description',
        'property',
        'innerText',
        'ar-instruction-step-1-description',
        'Make sure you have the Alexa app installed on your phone.'
    );
    translate(
        '#ar-instruction-step-2-description',
        'property',
        'innerText',
        'ar-instruction-step-2-description',
        'Open the camera app on your phone and scan the QR code with the camera.'
    );
    translate(
        '#ar-instruction-step-3-description',
        'property',
        'innerText',
        'ar-instruction-step-3-description',
        'Tap on the alert to open the Routine in the Alexa app.'
    );
    translate(
        '#ar-bottom-footer-need-more-help',
        'property',
        'innerText',
        'ar-bottom-footer-need-more-help',
        'Need more help?'
    );
    translate(
        '#ar-link-to-help-page',
        'property',
        'innerText',
        'ar-link-to-help-page',
        'View the help page for more information.'
    );
    translate(
        '#ar-bottom-header-mobile-continue-to-app',
        'property',
        'innerText',
        'ar-bottom-header-mobile-continue-to-app',
        'Continue to the Alexa App to view this Routine'
    );
    translate(
        '#ar-continue-button',
        'property',
        'innerText',
        'ar-continue-button',
        'Continue'
    );
    translate(
        '#ar-copy-link-widget-header-having-trouble',
        'property',
        'innerText',
        'ar-copy-link-widget-header-having-trouble',
        'Having trouble? Try opening this link in another browser.'
    );
    translate(
        '#ar-copy-link-widget-button',
        'property',
        'innerText',
        'ar-copy-link-widget-button',
        'Copy'
    );
    translate(
        '#ar-bottom-footer-mobile-dont-have-app',
        'property',
        'innerText',
        'ar-bottom-footer-mobile-dont-have-app',
        'Don\'t have the app?'
    );
    translate(
        '#ar-app-store-link-download-now',
        'property',
        'innerText',
        'ar-app-store-link-download-now',
        'Download now'
    );
}
